<template>
  <div class="face-enrich-section">
    <!-- Estados no-success -->
    <div v-if="state === 'idle'" class="face-enrich-section__idle">
      <v-icon size="36" color="var(--kln-text-dim)">mdi-face-recognition</v-icon>
      <p class="face-enrich-section__idle-text">
        Carga una foto y haz clic en "Buscar rostros" para ver los datos del rostro.
      </p>
    </div>

    <div v-else-if="state === 'loading'" class="face-enrich-section__placeholder">
      <v-progress-circular indeterminate size="20" width="2" color="var(--kln-accent)" />
      <span class="ml-2">{{ $vuetify.lang.t('$vuetify.face_search.enrich.loading') }}</span>
    </div>

    <div v-else-if="state === 'no_identifier'" class="face-enrich-section__info">
      <v-icon small>mdi-information-outline</v-icon>
      <span class="ml-2">{{ $vuetify.lang.t('$vuetify.face_search.enrich.no_identifier') }}</span>
    </div>

    <div v-else-if="state === 'no_photo'" class="face-enrich-section__info">
      <v-icon small>mdi-information-outline</v-icon>
      <span class="ml-2">{{ $vuetify.lang.t('$vuetify.face_search.filters.no_photo_yet') }}</span>
    </div>

    <div
      v-else-if="state === 'session_expired' || state === 'file_too_large' || state === 'enrich_failed'"
      class="face-enrich-section__error"
    >
      <v-icon small color="error">mdi-alert-circle-outline</v-icon>
      <span class="ml-2">{{ errorMessage }}</span>
      <v-btn
        v-if="canRetry"
        x-small
        outlined
        color="var(--kln-accent)"
        class="ml-2"
        @click="$emit('retry-enrich')"
      >
        {{ $vuetify.lang.t('$vuetify.face_search.enrich.retry') }}
      </v-btn>
    </div>

    <div
      v-else-if="state === 'success' && enrichment && !enrichment.fr"
      class="face-enrich-section__info"
    >
      <v-icon small>mdi-account-off-outline</v-icon>
      <span class="ml-2">{{ $vuetify.lang.t('$vuetify.face_search.enrich.no_match') }}</span>
    </div>

    <!-- Success con datos -->
    <div
      v-else-if="state === 'success' && enrichment && enrichment.fr"
      class="enrich-body"
    >
      <!-- Paginación entre personas (solo si hay varias) -->
      <div v-if="murphyPersonas.length > 1" class="enrich-pagination">
        <v-btn icon x-small :disabled="personaIndex === 0" @click="personaIndex -= 1">
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="enrich-pagination__label">
          {{ personaIndex + 1 }} de {{ murphyPersonas.length }}
        </span>
        <v-btn
          icon
          x-small
          :disabled="personaIndex === murphyPersonas.length - 1"
          @click="personaIndex += 1"
        >
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <!-- Header: foto + nombre -->
      <div class="enrich-header">
        <img
          v-if="headerPhotoSrc && !headerPhotoFailed"
          :src="headerPhotoSrc"
          class="enrich-header__photo"
          alt="Foto"
          @error="headerPhotoFailed = true"
        />
        <div v-else class="enrich-header__photo enrich-header__photo--fallback">
          <v-icon size="48">mdi-account</v-icon>
        </div>
        <div class="enrich-header__name">{{ fullName || '—' }}</div>
      </div>

      <!-- Datos clave en lista vertical -->
      <div class="enrich-keyfields">
        <div v-for="kf in keyFields" :key="kf.label" class="enrich-keyfield">
          <div class="enrich-keyfield__label">{{ kf.label }}</div>
          <div class="enrich-keyfield__value-row">
            <span class="enrich-keyfield__value">{{ kf.value }}</span>
            <v-btn
              v-if="kf.copyable"
              icon
              x-small
              class="enrich-keyfield__copy"
              :title="`Copiar ${kf.label}`"
              @click="copyToClipboard(kf.value)"
            >
              <v-icon x-small>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Banner: sin antecedentes registrados -->
      <div v-if="!hasRiskRecords" class="enrich-banner enrich-banner--ok">
        <v-icon small color="var(--kln-success, #58c46a)">mdi-check-circle-outline</v-icon>
        <span>Sin antecedentes registrados en las fuentes consultadas.</span>
      </div>

      <!-- Categorías -->
      <div v-for="cat in categories" :key="cat.key" class="enrich-category">
        <button
          type="button"
          class="enrich-category__head"
          :class="{ 'enrich-category__head--risk': cat.risk }"
          @click="toggleCategory(cat.key)"
        >
          <v-icon small class="enrich-category__chev" :class="{ 'enrich-category__chev--open': isCategoryOpen(cat.key) }">
            mdi-chevron-right
          </v-icon>
          <span class="enrich-category__label">{{ cat.label }}</span>
          <span class="enrich-category__count">({{ cat.records.length }})</span>
        </button>
        <div v-if="isCategoryOpen(cat.key)" class="enrich-category__body">
          <div
            v-for="(record, idx) in cat.records"
            :key="cat.key + '-rec-' + idx"
            class="enrich-record"
          >
            <div v-if="cat.records.length > 1 || record.subtitle" class="enrich-record__subtitle">
              {{ record.subtitle || record.title }}
            </div>
            <div
              v-for="prop in scalarProperties(record)"
              :key="prop.name + '-scalar'"
              class="enrich-field"
            >
              <span class="enrich-field__label">{{ prop.label || prop.name }}</span>
              <span class="enrich-field__value">{{ prop.value || '—' }}</span>
            </div>
            <div
              v-for="prop in photoProperties(record)"
              :key="prop.name + '-photo'"
              class="enrich-field enrich-field--photo"
            >
              <span class="enrich-field__label">{{ prop.label || prop.name }}</span>
              <img
                v-if="prop.value && !photoFailed[cat.key + '-' + idx + '-' + prop.name]"
                :src="resolvePhotoSrc(prop.value)"
                class="enrich-field__photo"
                :alt="prop.label || 'foto'"
                @error="onPropertyPhotoError(cat.key + '-' + idx + '-' + prop.name)"
              />
              <div v-else class="enrich-field__photo enrich-field__photo--fallback">
                <v-icon small>mdi-image-off</v-icon>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="murphyDescription" class="enrich-description">{{ murphyDescription }}</div>
    </div>
  </div>
</template>

<script>
const GLUE_PROPERTY_NAMES = ['nombre', 'apellido_1', 'apellido_2', 'fecha_nac']

// Mapa de inferencia: objectType.title (uppercased) → { key, label, risk }
// La categoría se elige por keyword. Cualquier título no mapeado va a 'otros'.
const CATEGORY_RULES = [
  { test: /MANDAMIENTO/, key: 'mandamientos', label: 'Mandamientos', risk: true },
  { test: /FALTA/, key: 'faltas', label: 'Faltas', risk: true },
  { test: /PENITENCIAR/, key: 'penitenciaria', label: 'Información penitenciaria', risk: true },
  { test: /VEHICULO/, key: 'vehiculos', label: 'Vehículos', risk: false },
  { test: /\bHIJOS\b/, key: 'hijos', label: 'Hijos', risk: false },
  { test: /MATRIMONIO/, key: 'matrimonios', label: 'Matrimonios', risk: false },
  { test: /DIVORCIO/, key: 'divorcios', label: 'Divorcios', risk: false },
  { test: /NACIMIENTO/, key: 'nacimientos', label: 'Nacimientos', risk: false },
  { test: /\bINE\b|REGISTRO INE/, key: 'ine', label: 'INE', risk: false },
  { test: /IMSS/, key: 'imss', label: 'IMSS', risk: false },
  { test: /ISSSTE/, key: 'issste', label: 'ISSSTE', risk: false },
  { test: /\bRNP\b/, key: 'rnp', label: 'RNP', risk: false },
  { test: /LICENCIA/, key: 'licencias', label: 'Licencias', risk: false },
  { test: /CREDENCIAL/, key: 'credencializacion', label: 'Credencialización', risk: false },
  { test: /RENILO/, key: 'renilo', label: 'RENILO', risk: false },
]

function categorizeTitle(title) {
  const up = String(title || '').toUpperCase()
  for (let i = 0; i < CATEGORY_RULES.length; i += 1) {
    if (CATEGORY_RULES[i].test.test(up)) return CATEGORY_RULES[i]
  }
  return { key: 'otros', label: 'Otros documentos', risk: false }
}

function findFirstPropValue(records, propName) {
  for (let i = 0; i < records.length; i += 1) {
    const props = (records[i] && records[i].properties) || []
    const found = props.find((p) => p.name === propName && p.value)
    if (found) return found.value
  }
  return ''
}

function calcAge(fechaNac) {
  if (!fechaNac) return ''
  const m = String(fechaNac).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return ''
  const birth = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  if (Number.isNaN(birth.getTime())) return ''
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  const md = now.getMonth() - birth.getMonth()
  if (md < 0 || (md === 0 && now.getDate() < birth.getDate())) age -= 1
  return age >= 0 ? age + ' años' : ''
}

export default {
  name: 'FaceEnrichSection',
  props: {
    state: { type: String, default: 'idle' },
    enrichment: { type: Object, default: null },
  },
  data() {
    return {
      personaIndex: 0,
      photoFailed: {},
      headerPhotoFailed: false,
      // Estado por defecto: ciertas categorías abiertas, otras cerradas.
      // El usuario puede toggle individualmente.
      categoryOpen: {
        credencializacion: true,
        ine: true,
        vehiculos: true,
        hijos: true,
        matrimonios: true,
        renilo: false,
        nacimientos: false,
        imss: false,
        issste: false,
        rnp: false,
        licencias: false,
        otros: false,
        mandamientos: true,
        faltas: true,
        penitenciaria: true,
        divorcios: false,
      },
    }
  },
  computed: {
    fr() {
      return (this.enrichment && this.enrichment.fr) || null
    },
    fullName() {
      if (!this.fr) return ''
      const parts = [this.fr.nombre, this.fr.apellido_paterno, this.fr.apellido_materno]
      return parts.filter(Boolean).join(' ')
    },
    murphyPersonas() {
      const murphy = this.enrichment && this.enrichment.murphy
      if (!murphy) return []
      const glueProps = (murphy.baseType && Array.isArray(murphy.baseType.glueProperties))
        ? murphy.baseType.glueProperties
        : []
      const rawQueryObjects = Array.isArray(murphy.queryObjects) ? murphy.queryObjects : []
      const queryObjects = [...rawQueryObjects].sort((a, b) => {
        const pa = (a.objectType && typeof a.objectType.priority === 'number') ? a.objectType.priority : Infinity
        const pb = (b.objectType && typeof b.objectType.priority === 'number') ? b.objectType.priority : Infinity
        return pa - pb
      })
      const groups = new Map()
      queryObjects.forEach((qo) => {
        const title = (qo.objectType && (qo.objectType.title || qo.objectType.name)) || ''
        const results = Array.isArray(qo.resultObjects) ? qo.resultObjects : []
        results.forEach((ro) => {
          const props = Array.isArray(ro.properties) ? ro.properties : []
          const glueValues = {}
          glueProps.forEach((g) => {
            const found = props.find((p) => p.name === g)
            glueValues[g] = found ? found.value : ''
          })
          const key = JSON.stringify(glueValues)
          if (!groups.has(key)) groups.set(key, { glueValues, records: [] })
          groups.get(key).records.push({ title, properties: props })
        })
      })
      return Array.from(groups.values())
    },
    currentPersona() {
      return this.murphyPersonas[this.personaIndex] || null
    },
    currentPersonaRecords() {
      return this.currentPersona ? this.currentPersona.records : []
    },
    glueValues() {
      return (this.currentPersona && this.currentPersona.glueValues) || {}
    },
    fechaNac() {
      return this.glueValues.fecha_nac || ''
    },
    edad() {
      return calcAge(this.fechaNac)
    },
    estadoOrigen() {
      return findFirstPropValue(this.currentPersonaRecords, 'estado')
        || findFirstPropValue(this.currentPersonaRecords, 'estado_nacimiento')
        || ''
    },
    curp() {
      return findFirstPropValue(this.currentPersonaRecords, 'curp')
    },
    rfc() {
      return findFirstPropValue(this.currentPersonaRecords, 'rfc')
    },
    keyFields() {
      const fields = []
      if (this.fullName) fields.push({ label: 'NOMBRE', value: this.fullName })
      if (this.fechaNac) fields.push({ label: 'NACIMIENTO', value: this.fechaNac })
      if (this.edad) fields.push({ label: 'EDAD', value: this.edad })
      if (this.estadoOrigen) fields.push({ label: 'ESTADO DE ORIGEN', value: this.estadoOrigen })
      if (this.curp) fields.push({ label: 'CURP', value: this.curp, copyable: true })
      if (this.rfc) fields.push({ label: 'RFC', value: this.rfc, copyable: true })
      return fields
    },
    headerPhotoProp() {
      const records = this.currentPersonaRecords
      for (let i = 0; i < records.length; i += 1) {
        const props = (records[i] && records[i].properties) || []
        const found = props.find((p) => p && p.type === 'Photo' && p.value)
        if (found) return found
      }
      return null
    },
    headerPhotoSrc() {
      return this.headerPhotoProp ? this.resolvePhotoSrc(this.headerPhotoProp.value) : ''
    },
    categories() {
      // Agrupar records por categoría inferida del título
      const buckets = new Map()
      this.currentPersonaRecords.forEach((record) => {
        const cat = categorizeTitle(record.title)
        if (!buckets.has(cat.key)) {
          buckets.set(cat.key, { ...cat, records: [] })
        }
        buckets.get(cat.key).records.push({ ...record, subtitle: record.title })
      })
      // Orden: riesgo primero, luego identidad, luego vehículos/familia, luego resto
      const order = [
        'mandamientos', 'faltas', 'penitenciaria',
        'credencializacion', 'ine', 'renilo',
        'vehiculos', 'hijos', 'matrimonios', 'divorcios', 'nacimientos',
        'imss', 'issste', 'rnp', 'licencias', 'otros',
      ]
      return Array.from(buckets.values()).sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
    },
    hasRiskRecords() {
      return this.categories.some((c) => c.risk)
    },
    murphyDescription() {
      return (this.enrichment && this.enrichment.murphy && this.enrichment.murphy.description) || ''
    },
    canRetry() {
      return this.state !== 'session_expired' && this.state !== 'no_identifier'
    },
    errorMessage() {
      if (this.state === 'session_expired') return this.$vuetify.lang.t('$vuetify.face_search.errors.session_expired')
      if (this.state === 'file_too_large') return this.$vuetify.lang.t('$vuetify.face_search.errors.file_too_large')
      return this.$vuetify.lang.t('$vuetify.face_search.enrich.failed')
    },
  },
  watch: {
    enrichment() {
      this.personaIndex = 0
      this.photoFailed = {}
      this.headerPhotoFailed = false
    },
    personaIndex() {
      this.photoFailed = {}
      this.headerPhotoFailed = false
    },
  },
  methods: {
    isHidden(prop) {
      if (GLUE_PROPERTY_NAMES.indexOf(prop.name) !== -1) return true
      const hp = this.headerPhotoProp
      if (hp && prop.type === 'Photo' && prop.name === hp.name) return true
      // Ocultar campos ya mostrados en keyFields para evitar duplicación
      const name = String(prop.name || '').toLowerCase()
      if (['curp', 'rfc'].includes(name)) return true
      return false
    },
    visibleProperties(record) {
      const props = (record && Array.isArray(record.properties)) ? record.properties : []
      return props.filter((p) => p && !this.isHidden(p))
    },
    scalarProperties(record) {
      return this.visibleProperties(record).filter((p) => p.type !== 'Photo')
    },
    photoProperties(record) {
      return this.visibleProperties(record).filter((p) => p.type === 'Photo')
    },
    resolvePhotoSrc(rawValue) {
      if (!rawValue || typeof rawValue !== 'string') return ''
      if (rawValue.startsWith('data:')) return rawValue
      if (rawValue.startsWith('http://') || rawValue.startsWith('https://')) return rawValue
      return `data:image/jpeg;base64,${rawValue}`
    },
    onPropertyPhotoError(key) {
      this.photoFailed = { ...this.photoFailed, [key]: true }
    },
    isCategoryOpen(key) {
      return !!this.categoryOpen[key]
    },
    toggleCategory(key) {
      this.$set(this.categoryOpen, key, !this.categoryOpen[key])
    },
    copyToClipboard(value) {
      if (!value) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).catch(() => {})
        return
      }
      try {
        const ta = document.createElement('textarea')
        ta.value = value
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      } catch (e) { /* ignore */ }
    },
  },
}
</script>

<style lang="scss" scoped>
.face-enrich-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
}

.face-enrich-section__placeholder,
.face-enrich-section__info,
.face-enrich-section__error {
  display: flex;
  align-items: center;
  color: var(--kln-text-secondary);
  font-size: 12px;
  padding: 8px 12px;
}

.face-enrich-section__idle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  text-align: center;
}

.face-enrich-section__idle-text {
  font-size: 12px;
  color: var(--kln-text-secondary);
  line-height: 1.4;
  margin: 0;
}

.enrich-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Paginación */
.enrich-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 0;
}

.enrich-pagination__label {
  font-size: 11px;
  color: var(--kln-text-secondary);
  letter-spacing: 0.5px;
}

/* Header */
.enrich-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 0 12px;
  border-bottom: 1px solid var(--kln-border-subtle);
}

.enrich-header__photo {
  width: 120px;
  height: 120px;
  border-radius: 6px;
  object-fit: cover;
  background: var(--kln-bg-deeper);
  border: 1px solid var(--kln-border-subtle);
}

.enrich-header__photo--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--kln-text-secondary);
}

.enrich-header__name {
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  color: var(--kln-text-primary);
  word-break: break-word;
  padding: 0 8px;
}

/* Datos clave */
.enrich-keyfields {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 4px;
}

.enrich-keyfield__label {
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--kln-text-dim);
  margin-bottom: 2px;
}

.enrich-keyfield__value-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.enrich-keyfield__value {
  font-size: 14px;
  color: var(--kln-text);
  font-weight: 600;
  flex: 1;
  word-break: break-word;
}

.enrich-keyfield__copy {
  flex-shrink: 0;
  color: var(--kln-text-secondary);
}

/* Banner sin antecedentes */
.enrich-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 12px;
}

.enrich-banner--ok {
  background: rgba(88, 196, 106, 0.1);
  color: var(--kln-success, #58c46a);
  border-left: 3px solid var(--kln-success, #58c46a);
}

/* Categorías */
.enrich-category {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--kln-border-subtle);
}

.enrich-category__head {
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  padding: 10px 4px;
  cursor: pointer;
  font: inherit;
  color: var(--kln-text-primary);
  text-align: left;
  width: 100%;
}

.enrich-category__head:hover {
  background: var(--kln-border-dim);
}

.enrich-category__head--risk {
  color: var(--kln-error, #ef5350);
}

.enrich-category__chev {
  color: var(--kln-text-secondary) !important;
  transition: transform 0.15s ease;
}

.enrich-category__chev--open {
  transform: rotate(90deg);
}

.enrich-category__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.enrich-category__count {
  font-size: 11px;
  color: var(--kln-text-secondary);
  margin-left: 2px;
}

.enrich-category__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 8px 12px 24px;
}

.enrich-record {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 8px;
  border-bottom: 1px dashed var(--kln-border-subtle);
}

.enrich-record:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.enrich-record__subtitle {
  font-size: 11px;
  font-weight: 700;
  color: var(--kln-text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--kln-border-dim);
}

.enrich-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.enrich-field__label {
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--kln-text-dim);
}

.enrich-field__value {
  font-size: 13px;
  font-weight: 500;
  color: var(--kln-text);
  word-break: break-word;
  line-height: 1.35;
}

.enrich-field__photo {
  max-width: 100px;
  max-height: 130px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid var(--kln-border-subtle);
  background: var(--kln-bg-deeper);
}

.enrich-field__photo--fallback {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--kln-text-secondary);
}

.enrich-description {
  margin-top: 8px;
  padding: 6px 8px;
  background: var(--kln-bg-deeper);
  border-radius: 4px;
  font-size: 11px;
  color: var(--kln-text-secondary);
  line-height: 1.4;
}
</style>
